import { useEffect, useState } from "react";
import { Snowflake, X } from "lucide-react";

// Holiday banner — flip `enabled` false off-season, update the deadline each year.
const HOLIDAY_BANNER = {
  enabled: true,
  deadline: "December 5th",
};

const LINKS = [
  { href: "#story", label: "Our Story", testId: "nav-link-story" },
  { href: "#collection", label: "Collection", testId: "nav-link-collection" },
  { href: "#gallery", label: "Gallery", testId: "nav-link-gallery" },
  { href: "#reviews", label: "Reviews", testId: "nav-link-reviews" },
  { href: "#how-it-works", label: "How It Works", testId: "nav-link-how-it-works" },
  { href: "#patterns", label: "Patterns", testId: "nav-link-patterns" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [showBanner, setShowBanner] = useState(HOLIDAY_BANNER.enabled);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="fixed top-0 z-50 w-full">
      {showBanner && (
        <div data-testid="seasonal-banner" className="flex items-center justify-center gap-3 bg-[#2C2520] px-4 py-2.5">
          <Snowflake className="h-3.5 w-3.5 shrink-0 text-[#8FD5CB]" />
          <p className="text-center text-xs text-[#FDFBF7]/85 sm:text-sm">
            Handmade for the holidays? <span className="font-semibold text-[#8FD5CB]">Gift orders close {HOLIDAY_BANNER.deadline}</span> so every stitch arrives on time.
          </p>
          <a
            href="#order"
            data-testid="seasonal-banner-cta"
            className="hidden shrink-0 rounded-full bg-[#8FD5CB] px-3.5 py-1 text-xs font-semibold text-[#2C2520] transition-colors duration-300 hover:bg-[#FDFBF7] sm:inline-block"
          >
            Order early
          </a>
          <button
            onClick={() => setShowBanner(false)}
            aria-label="Dismiss holiday banner"
            data-testid="seasonal-banner-dismiss"
            className="shrink-0 text-[#FDFBF7]/50 transition-colors duration-300 hover:text-[#FDFBF7]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <header
        data-testid="site-header"
        className={`w-full border-b transition-[background-color,box-shadow,border-color] duration-500 ${
          scrolled || showBanner ? "bg-[#FDFBF7]/90 backdrop-blur-md border-[#E6DFD5] shadow-[0_8px_30px_rgba(44,37,32,0.06)]" : "bg-transparent border-transparent"
        }`}
      >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8 lg:px-16">
        <a href="#top" data-testid="nav-logo" className="group flex items-center gap-3 text-[#2C2520]">
          <img src="/logo.jpg" alt="Double Treble Stitches logo" className="h-10 w-10 rounded-lg object-cover shadow-sm transition-transform duration-500 group-hover:rotate-[6deg]" />
          <span className="font-serif text-xl font-semibold leading-none tracking-tight">
            Double Treble <span className="italic text-[#A35C45]">Stitches</span>
          </span>
        </a>
        <nav className="hidden items-center gap-5 md:flex lg:gap-7">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testId}
              className="font-serif text-[16px] font-medium text-[#6E6259] transition-colors duration-300 hover:italic hover:text-[#A35C45]"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <a
          href="#order"
          data-testid="nav-cta-order"
          className="rounded-full bg-[#2C2520] px-5 py-2.5 text-sm font-medium text-[#FDFBF7] transition-colors duration-300 hover:bg-[#A35C45]"
        >
          Start an Order
        </a>
      </div>
      </header>
    </div>
  );
}
